type AttachmentDocType = 'docx' | 'xlsx' | 'pdf' | 'csv' | 'tsv' | 'txt' | 'md' | 'json';

type AttachmentStatus = 'uploading' | 'ready' | 'error';

type PendingAttachment = {
  localId: string;
  filename: string;
  docType: AttachmentDocType;
  sizeBytes: number;
  status: AttachmentStatus;
  documentId?: string;
  errorMessage?: string;
};

type AttachmentValidation = { ok: true; docType: AttachmentDocType } | { ok: false; error: string };

const MAX_ATTACHMENT_BYTES = 25 * 1024 * 1024;
const MAX_ATTACHMENTS = 5;

/** Extension → doc type, in the order shown to the file picker. Mirrors the backend extractor registry. */
const ATTACHMENT_EXTENSION_TO_DOC_TYPE: Record<string, AttachmentDocType> = {
  '.docx': 'docx',
  '.xlsx': 'xlsx',
  '.pdf': 'pdf',
  '.csv': 'csv',
  '.tsv': 'tsv',
  '.txt': 'txt',
  '.md': 'md',
  '.json': 'json',
};

/** Comma-separated `accept` value for the attachment file picker. */
const ACCEPTED_ATTACHMENT_EXTENSIONS = Object.keys(ATTACHMENT_EXTENSION_TO_DOC_TYPE).join(',');

const UNSUPPORTED_ATTACHMENT_MESSAGE = 'Supported files: Word, Excel, PDF, CSV, TSV, text, Markdown, and JSON.';

/**
 * Maps a filename to a supported doc type by its extension.
 *
 * @param {string} filename - The uploaded file's name
 * @returns {AttachmentDocType | null} The doc type, or null when unsupported
 */
function resolveAttachmentDocType(filename: string): AttachmentDocType | null {
  const lower = filename.toLowerCase();
  for (const [extension, docType] of Object.entries(ATTACHMENT_EXTENSION_TO_DOC_TYPE)) {
    if (lower.endsWith(extension)) return docType;
  }
  return null;
}

/**
 * Validates a candidate attachment against the same rules the backend
 * enforces (supported extension, 25 MB cap) plus a client-side count cap.
 *
 * @param {{ name: string; size: number }} file - The file to validate
 * @param {number} existingCount - How many attachments are already staged
 * @returns {AttachmentValidation} An ok result with the doc type, or an error
 */
function validateAttachmentFile(file: { name: string; size: number }, existingCount: number): AttachmentValidation {
  if (existingCount >= MAX_ATTACHMENTS) {
    return { ok: false, error: `You can attach up to ${MAX_ATTACHMENTS} files.` };
  }
  const docType = resolveAttachmentDocType(file.name);
  if (!docType) {
    return { ok: false, error: UNSUPPORTED_ATTACHMENT_MESSAGE };
  }
  if (file.size > MAX_ATTACHMENT_BYTES) {
    return { ok: false, error: 'File exceeds the 25 MB upload limit.' };
  }
  return { ok: true, docType };
}

// NOTE: there is deliberately no client-side "reference block" builder here.
// The backend owns that instruction: `_build_attachment_context` resolves the
// attached ids against the chat and user, and builds the hint from the FILENAME
// IT HAS STORED. Rebuilding it here would both duplicate the hint and splice an
// unsanitised `File.name` (attacker-controllable, newlines included) into an LLM
// instruction — the backend version avoids exactly that by never trusting the
// client's filename. Attachments travel as `document_ids` on the askAgent
// payload; that is the whole contract.

export {
  ACCEPTED_ATTACHMENT_EXTENSIONS,
  MAX_ATTACHMENTS,
  MAX_ATTACHMENT_BYTES,
  resolveAttachmentDocType,
  validateAttachmentFile,
};
export type { AttachmentDocType, AttachmentStatus, AttachmentValidation, PendingAttachment };
